import React from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { push } from "react-router-redux";

import { InjectedRouter, Link } from "react-router";
import { apiPromiseHook, createEnterHookPromise } from "../../../../shared/flux/actions/enterHooks";
import { StoreActionFactory } from "../../../../shared/state/storeActionFactory";
import { LocationNavigation } from "../../../components/location/locationNavigation";
import { ActionPromises } from "../../../state/actionPromises";
import { EnterHookPromises } from "../../../state/enterHookPromises";
import { Loader } from "./../../../../core/atoms/atoms";
import { HeroSection } from "@travellocal/ui";
import { MatchState } from "../../../../shared/helpers/reduxHelpers";
import * as WebRoutes from "@travellocal/utils/webRoutes";

import "./region.scss";

interface StateProps {
  regionState: IState<ILocation>;
  regionCountries: IState<ICountrySummary[]>;
}

interface DispatchProps {
  navigateToPath: (path: string) => void;
}

interface OwnProps {
  router: InjectedRouter;
}

type Props = StateProps & DispatchProps & OwnProps;

export class RegionIndexComponent extends React.Component<Props, {}> {

  private generateNavItems = (region) => {
    const navItems = [
      {
        to: WebRoutes.region(region.slug),
        title: "Overview",
        onlyActiveOnIndex: true,
      },
    ];

    if (region.images.length !== 0) {
      navItems.push({
        to: WebRoutes.regionGallery(region.slug),
        title: "Gallery",
        onlyActiveOnIndex: false,
      });
    }

    return navItems;
  }

  public render() {
    const { regionState } = this.props;

    return MatchState(regionState, {
      pending: () => <Loader />,
      error: () => (
        <div className="width-wrapper p-top-large">
          <h1>Sorry! Something went wrong and we can't show you this page.</h1>
          <p><Link to={WebRoutes.destinations()}>Click here to see all the destinations we offer</Link></p>
        </div>
      ),
      ok: () => {
        const region = regionState.data;

        return (
          <div className="region-index">
            <Helmet title={region.titleTag || region.name}
              meta={[
                { name: "description", content: region.descriptionTag },
              ]}
              link={[
                { rel: "canonical", href: `${globalConfig.wwwBaseUrl}${WebRoutes.region(region.slug)}` },
              ]}
              script={[{
                type: "application/ld+json",
                innerHTML: `{
              "@context": "http://schema.org",
              "@type": "Continent",
              "name": "${region.name}",
              "image": "${region.heroImage ? region.heroImage.largeUrl : ""}",
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": "${region.geoLocation.latitude}",
                "longitude": "${region.geoLocation.longitude}"
              },
              "sameAs": "https://en.wikipedia.org/wiki/${region.name}"
          }`,
              }]} />

            <div className={region.images.length === 0 ? "no-tab-spacing" : ""}>
              <HeroSection images={region.images} heroImage={region.heroImage}>
                <h1>{region.name}</h1>
              </HeroSection>

              {region.images.length !== 0 &&
                <LocationNavigation
                  items={this.generateNavItems(region)}
                  onSelectChange={route => {
                    this.props.router.push(route);
                  }}
                />
              }

              {this.props.children}
            </div>
          </div>
        )
      }
    }) as JSX.Element;
  }
}

const mapStateToProps = (state: IWebStoreState): StateProps => {
  return {
    regionState: state.region,
    regionCountries: state.regionCountries,
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => {
  return {
    navigateToPath: (path: string) => dispatch(push(path)),
  };
};

export const RegionIndex = connect(mapStateToProps, mapDispatchToProps)(RegionIndexComponent);

export const regionIndexOnEnter = apiPromiseHook<any>(
  EnterHookPromises.countriesByRegionSlug("regionSlug", true),
  createEnterHookPromise((router) => {
    const regionSlug = router.params.regionSlug;

    return StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {
      const { region, contentByCollection } = getState();

      if (!region
        || !region.data
        || region.data.slug !== regionSlug) {

        const should404 = true;

        await dispatch(ActionPromises.regionBySlug(regionSlug, LocationType.Region, should404));
      }
    });
  }),
);
